import { z } from 'zod';

import type { OrderItem, RequestedChangeLine } from './api';
import { menuLineFormSchema, type MenuLineFormValues } from './menu-line-form-schema';

/**
 * Mirrors `apps/api` `createChangeRequestSchema` (`change-request.dto.ts`):
 * `items` is the full desired list of lines by position
 * (`rotating-menu/design.md` §3.4), not a line-by-line diff
 * (`change-request.repository.ts`'s own doc comment on
 * `RequestedChanges`). Rows are the order's active positions, fixed like
 * `OrderForm`'s — "Agregá al menos una línea" is enforced the same way,
 * requiring at least one row with a quantity greater than zero.
 */
export const changeRequestFormSchema = z.object({ items: z.array(menuLineFormSchema) }).superRefine(
  (data, ctx) => {
    const hasQuantity = data.items.some((item) => item.quantity !== '' && Number(item.quantity) > 0);

    if (!hasQuantity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Agregá al menos una línea',
        path: ['items'],
      });
    }
  },
);

export type ChangeRequestFormValues = z.infer<typeof changeRequestFormSchema>;

/** Only items with a frozen `positionId` are editable rows (`rotating-menu/design.md` §1.4, T6); legacy items with no position cannot be requested by position and are dropped, same choice `order-form-schema.ts`'s `legacyOrderItems` documents. */
export function toChangeRequestFormValues(items: OrderItem[]): ChangeRequestFormValues {
  return {
    items: items
      .filter((item): item is OrderItem & { positionId: string } => item.positionId !== null)
      .map((item) => ({
        positionId: item.positionId,
        quantity: String(item.quantity),
        notes: item.notes ?? '',
      })),
  };
}

export function toRequestedChangeLines(values: ChangeRequestFormValues): RequestedChangeLine[] {
  return toItemInputs(values.items);
}

function toItemInputs(items: MenuLineFormValues[]): RequestedChangeLine[] {
  return items
    .filter((item) => item.quantity !== '' && Number(item.quantity) > 0)
    .map((item) => ({
      positionId: item.positionId,
      quantity: Number(item.quantity),
      notes: item.notes === '' ? null : item.notes,
    }));
}
